/* ═══════════════════════════════════════════════════════════════════════════
   scene3d.js  —  three.js background scenes, same pattern as animations.js

   DATA ATTRIBUTES (mirrors data-anim's contract in animations.js):
     data-scene3d="ambient"           which scene to build — see SCENES below
                                      for the full list (ambient, starfield,
                                      constellation, aurora-mesh, radial-glow)
     data-scene3d-accent="#a8672f"    literal accent color — the section-level
                                      bg_color escape hatch's counterpart, since
                                      this site's --rfhq-color-primary token
                                      renders as brand red/orange, not whatever
                                      color a given scene actually needs (see
                                      project_rfhq_demo_script_library memory —
                                      never assume a theme token's color from
                                      its name on this site)
     data-scene3d-base="#1c2b4a"      literal base/dark color, same reasoning

   In normal use these attributes are emitted automatically by
   templates/includes/rfhq/backdrop.html from the Scene3D/Scene3D Base
   Colour/Scene3D Accent Colour fields on RFHQ Section — a content editor
   configures this entirely through the Section form (Select + two Color
   pickers), the same as Texture/Pattern above it.

   Three.js itself is REFERENCED, not bundled: loadThree()/loadPostprocessing()
   resolve runtime import() calls against the vendored ES modules at their own
   asset paths, built from a non-literal expression (see assetUrl) so esbuild
   leaves them alone as genuine runtime imports instead of trying and failing
   to resolve them at build time — no plugin here marks /assets/* external for
   JS bundles, unlike the CSS build's ignore-assets plugin. Costs nothing on
   pages with no [data-scene3d] element, matching the on-demand behaviour
   ensureSwiper()/ensureModelViewer() already give Swiper and model-viewer,
   just without a bespoke loader class. The postprocessing addons
   (public/js/vendor/three/examples/jsm/postprocessing/*.js) are vendored
   unmodified from upstream three.js and import the bare specifier "three",
   resolved via the <script type="importmap"> injected site-wide by
   Website HQ Settings.get_website_context(), next to window.AnimSchema.

   Every scene below follows the same hard safety rules, each one a real bug
   hit and fixed building the earlier demos and first live versions (see
   project_scene3d_threejs_debugging + project_scene3d_live_implementation
   memory for the full story):
     - size() runs before any renderer/composer construction — a canvas with
       no real size yet reads as 0×0, producing an incomplete framebuffer.
     - any animated per-vertex plane color uses ONE wave term for hue-mix and
       a DIFFERENT, non-zero-floored range for brightness — coupling them to
       the same value means the base color only ever appears at its own
       dimmest point, and a two-wave PRODUCT correlates over broad regions
       and reads as a flat white blob instead of a glow.
     - intensity is always hard-capped well under 1 by construction (a floor
       + a bounded gain), so nothing can saturate regardless of camera
       position or elapsed time.
     - dense meshes stay FrontSide + NormalBlending; AdditiveBlending is only
       used on sparse, isolated points/objects, where overdraw can't compound.
     - bloom's threshold sits above every scene's own intensity ceiling, so it
       highlights the one deliberately-bright element (a glow core, a bright
       particle) instead of washing the whole scene into a soft blur.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function assetUrl(path) {
    return '/assets/raftor_websitehq/' + path;
  }

  var threeReady = null;
  function loadThree() {
    if (!threeReady) threeReady = import(assetUrl('js/vendor/three.module.min.js'));
    return threeReady;
  }

  var postReady = null;
  function loadPostprocessing() {
    if (!postReady) {
      var base = 'js/vendor/three/examples/jsm/postprocessing/';
      postReady = Promise.all([
        loadThree(),
        import(assetUrl(base + 'EffectComposer.js')),
        import(assetUrl(base + 'RenderPass.js')),
        import(assetUrl(base + 'UnrealBloomPass.js')),
      ]).then(function (mods) {
        return { THREE: mods[0], EffectComposer: mods[1].EffectComposer, RenderPass: mods[2].RenderPass, UnrealBloomPass: mods[3].UnrealBloomPass };
      });
    }
    return postReady;
  }

  // ── Shared bloom lifecycle — every scene below uses the same three calls ──
  function attachBloom(renderer, scene, camera, canvas, bloomOpts) {
    var state = { composer: null, bloom: null, disposed: false };
    loadPostprocessing().then(function (mods) {
      if (state.disposed) return;
      try {
        var w = canvas.clientWidth || 800, h = canvas.clientHeight || 500;
        var composer = new mods.EffectComposer(renderer);
        composer.addPass(new mods.RenderPass(scene, camera));
        var bloom = new mods.UnrealBloomPass(
          new mods.THREE.Vector2(w, h), bloomOpts.strength, bloomOpts.radius, bloomOpts.threshold
        );
        composer.addPass(bloom);
        state.composer = composer; state.bloom = bloom;
      } catch (err) { /* leave null — render() below falls back to plain rendering */ }
    });
    return state;
  }
  // `state` guards against null here on purpose: every scene calls size()
  // once synchronously before bloomState = attachBloom(...) has run (the
  // variable is still its initial `null` at that point, not yet the object
  // attachBloom returns) — first bug found this pass, via an unguarded
  // "Cannot read properties of null (reading 'composer')".
  function resizeBloom(state, w, h) { if (state && state.composer) state.composer.setSize(w, h); }
  function renderWithBloom(renderer, scene, camera, state) {
    if (state && state.composer) state.composer.render(); else renderer.render(scene, camera);
  }
  function disposeBloom(state) {
    state.disposed = true;
    // r128's EffectComposer exposes no public .dispose() — only its internal
    // renderTarget1/renderTarget2 do (see project_scene3d_threejs_debugging
    // memory). UnrealBloomPass does have its own .dispose().
    if (state.bloom) state.bloom.dispose();
    if (state.composer) {
      if (state.composer.renderTarget1) state.composer.renderTarget1.dispose();
      if (state.composer.renderTarget2) state.composer.renderTarget2.dispose();
    }
  }

  function themeColor(el, attr, cssVar, fallback) {
    var literal = el.dataset[attr];
    if (literal) return literal;
    var v = getComputedStyle(el).getPropertyValue(cssVar).trim();
    return v || fallback;
  }

  function damp(current, target, lambda, dt) {
    return current + (target - current) * (1 - Math.exp(-lambda * dt));
  }

  // A single wave-modulated plane layer, shared by "ambient" and
  // "aurora-mesh". See the file header for why hue-mix and brightness use
  // different ranges of the wave, and why intensity (floor+gain) stays
  // hard-capped well under 1.
  function makeWaveLayer(THREE, scene, opts) {
    var geo = new THREE.PlaneGeometry(opts.w, opts.h, opts.segW, opts.segH);
    var posAttr = geo.getAttribute('position');
    var base = Float32Array.from(posAttr.array);
    var colorArr = new Float32Array(posAttr.count * 3);
    geo.setAttribute('color', new THREE.BufferAttribute(colorArr, 3));

    var mat = new THREE.MeshBasicMaterial({
      vertexColors: true, transparent: true, opacity: opts.opacity,
      blending: THREE.NormalBlending, side: THREE.FrontSide, depthWrite: false,
    });
    var mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(opts.x || 0, opts.y || 0, opts.z || 0);
    if (opts.rotX) mesh.rotation.x = opts.rotX;
    scene.add(mesh);

    var cBase = new THREE.Color(opts.base), cAccent = new THREE.Color(opts.accent), tmp = new THREE.Color();

    return {
      mesh: mesh,
      update: function (t) {
        var arr = posAttr.array;
        for (var i = 0; i < arr.length; i += 3) {
          var primary = opts.axis === 'v' ? base[i + 1] : base[i];
          var w1 = Math.sin(primary * opts.freq + t * opts.speed);
          arr[i + 2] = base[i + 2] + w1 * opts.amp;

          var glow = Math.max(0, w1);
          tmp.copy(cBase).lerp(cAccent, glow * 0.5);
          var intensity = opts.floor + glow * opts.gain;
          colorArr[i] = tmp.r * intensity; colorArr[i + 1] = tmp.g * intensity; colorArr[i + 2] = tmp.b * intensity;
        }
        posAttr.needsUpdate = true;
        geo.attributes.color.needsUpdate = true;
      },
      dispose: function () { geo.dispose(); mat.dispose(); },
    };
  }

  // ── ambient: two crossing wave-planes, drifting motes, a focal glow core ──
  function buildAmbient(THREE, canvas, el) {
    var baseHex = themeColor(el, 'scene3dBase', '--rfhq-color-bg', '#2a3f66');
    var accentHex = themeColor(el, 'scene3dAccent', '--rfhq-color-primary', '#a8672f');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 0, 7.5);

    var bloomState = null;
    function size() {
      var w = canvas.clientWidth || el.clientWidth || 800;
      var h = canvas.clientHeight || el.clientHeight || 500;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      resizeBloom(bloomState, w, h);
    }
    size();

    var warp = makeWaveLayer(THREE, scene, { axis: 'v', freq: 0.75, speed: 0.42, amp: 0.6, z: 0.25, w: 22, h: 13, segW: 44, segH: 26, opacity: 0.9, base: baseHex, accent: accentHex, floor: 0.45, gain: 0.32 });
    var weft = makeWaveLayer(THREE, scene, { axis: 'h', freq: 0.85, speed: -0.34, amp: 0.45, z: -0.25, w: 22, h: 13, segW: 44, segH: 26, opacity: 0.55, base: accentHex, accent: baseHex, floor: 0.3, gain: 0.3 });

    var glowGeo = new THREE.SphereGeometry(0.22, 16, 16);
    var glowMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(accentHex), transparent: true, opacity: 0.9 });
    var glowCore = new THREE.Mesh(glowGeo, glowMat);
    glowCore.position.set(2.6, 1.4, 1.2);
    scene.add(glowCore);

    var DUST_COUNT = 260;
    var dustPos = new Float32Array(DUST_COUNT * 3);
    var dustCol = new Float32Array(DUST_COUNT * 3);
    var dustSpeed = new Float32Array(DUST_COUNT);
    var cAccentDust = new THREE.Color(accentHex), cWhite = new THREE.Color(0xffffff), cMote = new THREE.Color();
    for (var d = 0; d < DUST_COUNT; d++) {
      dustPos[d * 3] = (Math.random() - 0.5) * 20;
      dustPos[d * 3 + 1] = (Math.random() - 0.5) * 12;
      dustPos[d * 3 + 2] = (Math.random() - 0.5) * 3 + 1.8;
      dustSpeed[d] = 0.08 + Math.random() * 0.2;
      cMote.copy(cAccentDust).lerp(cWhite, Math.random() * 0.7);
      dustCol[d * 3] = cMote.r; dustCol[d * 3 + 1] = cMote.g; dustCol[d * 3 + 2] = cMote.b;
    }
    var dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    dustGeo.setAttribute('color', new THREE.BufferAttribute(dustCol, 3));
    var dustMat = new THREE.PointsMaterial({
      vertexColors: true, size: 0.06, transparent: true, opacity: 0.85,
      depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    });
    var dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    var mouse = { x: 0, y: 0 };
    function onMove(e) {
      var r = el.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) - 0.5;
      mouse.y = ((e.clientY - r.top) / r.height) - 0.5;
    }
    el.addEventListener('pointermove', onMove);

    bloomState = attachBloom(renderer, scene, camera, canvas, { strength: 0.9, radius: 0.4, threshold: 0.7 });

    var raf = null, lastFrame = null;
    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.1) : 0.016;
      lastFrame = now;
      var t = now / 1000;

      warp.update(t); weft.update(t);

      var arr = dustPos;
      for (var i = 0; i < DUST_COUNT; i++) {
        arr[i * 3 + 1] += dustSpeed[i] * dt;
        if (arr[i * 3 + 1] > 6.5) arr[i * 3 + 1] = -6.5;
      }
      dustGeo.attributes.position.needsUpdate = true;

      glowCore.position.x = 2.6 + Math.sin(t * 0.25) * 0.6;
      glowCore.position.y = 1.4 + Math.cos(t * 0.2) * 0.4;
      glowCore.scale.setScalar(1 + Math.sin(t * 1.4) * 0.15);

      camera.position.x = damp(camera.position.x, mouse.x * 1.1, 2, dt);
      camera.position.y = damp(camera.position.y, -mouse.y * 0.7, 2, dt);
      camera.lookAt(0, 0, 0);

      renderWithBloom(renderer, scene, camera, bloomState);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(size) : null;
    if (ro) ro.observe(el);

    return {
      start: function () { if (raf === null) { lastFrame = null; raf = requestAnimationFrame(frame); } },
      stop: function () { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } },
      dispose: function () {
        this.stop();
        el.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        warp.dispose(); weft.dispose();
        dustGeo.dispose(); dustMat.dispose();
        glowGeo.dispose(); glowMat.dispose();
        disposeBloom(bloomState);
        renderer.dispose();
      },
    };
  }

  // ── starfield: three depth layers of points, parallax + bloom ───────────
  function buildStarfield(THREE, canvas, el) {
    var baseHex = themeColor(el, 'scene3dBase', '--rfhq-color-bg', '#0e1730');
    var accentHex = themeColor(el, 'scene3dAccent', '--rfhq-color-primary', '#a8672f');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(60, 1, 0.1, 200);
    camera.position.z = 9;

    var bloomState = null;
    function size() {
      var w = canvas.clientWidth || el.clientWidth || 800;
      var h = canvas.clientHeight || el.clientHeight || 500;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      resizeBloom(bloomState, w, h);
    }
    size();

    var cWhite = new THREE.Color(0xffffff), cAccent = new THREE.Color(accentHex), cBase = new THREE.Color(baseHex).lerp(cWhite, 0.4);
    var layers = [];
    var group = new THREE.Group();
    scene.add(group);
    [
      { count: 220, rMin: 20, rMax: 60, size: 0.06, opacity: 0.4, speed: 0.002 },
      { count: 140, rMin: 8, rMax: 20, size: 0.11, opacity: 0.65, speed: 0.005 },
      { count: 26, rMin: 3, rMax: 8, size: 0.2, opacity: 1, speed: 0.01 },
    ].forEach(function (spec) {
      var pos = new Float32Array(spec.count * 3);
      var col = new Float32Array(spec.count * 3);
      for (var i = 0; i < spec.count; i++) {
        var r = spec.rMin + Math.random() * (spec.rMax - spec.rMin);
        var theta = Math.random() * Math.PI * 2, phi = Math.acos(2 * Math.random() - 1);
        pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.6;
        pos[i * 3 + 2] = r * Math.cos(phi);
        var c = Math.random() > 0.9 ? cAccent : (Math.random() > 0.5 ? cWhite : cBase);
        col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
      }
      var geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      var mat = new THREE.PointsMaterial({
        size: spec.size, vertexColors: true, transparent: true, opacity: spec.opacity,
        depthWrite: false, sizeAttenuation: true, blending: THREE.AdditiveBlending,
      });
      var points = new THREE.Points(geo, mat);
      points.userData.speed = spec.speed;
      group.add(points);
      layers.push({ points: points, geo: geo, mat: mat });
    });

    var mouse = { x: 0, y: 0 };
    function onMove(e) {
      var r = el.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) - 0.5;
      mouse.y = ((e.clientY - r.top) / r.height) - 0.5;
    }
    el.addEventListener('pointermove', onMove);

    bloomState = attachBloom(renderer, scene, camera, canvas, { strength: 0.7, radius: 0.45, threshold: 0.5 });

    var raf = null, lastFrame = null;
    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.1) : 0.016;
      lastFrame = now;

      layers.forEach(function (l) { l.points.rotation.y += l.points.userData.speed * dt * 30; });

      camera.position.x = damp(camera.position.x, mouse.x * 1.6, 3, dt);
      camera.position.y = damp(camera.position.y, -mouse.y * 1.0, 3, dt);
      camera.lookAt(0, 0, 0);

      renderWithBloom(renderer, scene, camera, bloomState);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(size) : null;
    if (ro) ro.observe(el);

    return {
      start: function () { if (raf === null) { lastFrame = null; raf = requestAnimationFrame(frame); } },
      stop: function () { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } },
      dispose: function () {
        this.stop();
        el.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        layers.forEach(function (l) { l.geo.dispose(); l.mat.dispose(); });
        disposeBloom(bloomState);
        renderer.dispose();
      },
    };
  }

  // ── constellation: proximity-linked nodes, orbiting camera ──────────────
  function buildConstellation(THREE, canvas, el) {
    var baseHex = themeColor(el, 'scene3dBase', '--rfhq-color-bg', '#1c2b4a');
    var accentHex = themeColor(el, 'scene3dAccent', '--rfhq-color-primary', '#a8672f');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);

    var bloomState = null;
    function size() {
      var w = canvas.clientWidth || el.clientWidth || 800;
      var h = canvas.clientHeight || el.clientHeight || 500;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      resizeBloom(bloomState, w, h);
    }
    size();

    var group = new THREE.Group();
    scene.add(group);

    var N = 42;
    var nodeGeo = new THREE.SphereGeometry(0.055, 10, 10);
    var accentColor = new THREE.Color(accentHex), baseColor = new THREE.Color(baseHex).lerp(new THREE.Color(0xffffff), 0.5);
    var nodes = [];
    for (var i = 0; i < N; i++) {
      var mat = new THREE.MeshBasicMaterial({ color: i % 7 === 0 ? accentColor : baseColor });
      var mesh = new THREE.Mesh(nodeGeo, mat);
      mesh.position.set((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6);
      mesh.userData.drift = new THREE.Vector3((Math.random() - 0.5) * 0.14, (Math.random() - 0.5) * 0.14, (Math.random() - 0.5) * 0.14);
      mesh.userData.phase = Math.random() * Math.PI * 2;
      group.add(mesh);
      nodes.push(mesh);
    }

    var THRESH = 2.6;
    var pairs = [];
    for (var a = 0; a < N; a++) for (var b = a + 1; b < N; b++) {
      if (nodes[a].position.distanceTo(nodes[b].position) < THRESH) pairs.push([a, b]);
    }
    var linePos = new Float32Array(pairs.length * 6);
    var lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.BufferAttribute(linePos, 3));
    var lineMat = new THREE.LineBasicMaterial({ color: accentHex, transparent: true, opacity: 0.3 });
    var lineSegments = new THREE.LineSegments(lineGeo, lineMat);
    group.add(lineSegments);

    function syncLines() {
      var arr = lineGeo.attributes.position.array;
      for (var k = 0; k < pairs.length; k++) {
        var pa = nodes[pairs[k][0]].position, pb = nodes[pairs[k][1]].position;
        arr[k * 6] = pa.x; arr[k * 6 + 1] = pa.y; arr[k * 6 + 2] = pa.z;
        arr[k * 6 + 3] = pb.x; arr[k * 6 + 4] = pb.y; arr[k * 6 + 5] = pb.z;
      }
      lineGeo.attributes.position.needsUpdate = true;
    }
    syncLines();

    var mouse = { x: 0, y: 0 };
    function onMove(e) {
      var r = el.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) - 0.5;
      mouse.y = ((e.clientY - r.top) / r.height) - 0.5;
    }
    el.addEventListener('pointermove', onMove);

    bloomState = attachBloom(renderer, scene, camera, canvas, { strength: 0.6, radius: 0.5, threshold: 0.55 });

    var raf = null, lastFrame = null;
    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.1) : 0.016;
      lastFrame = now;
      var t = now / 1000;

      nodes.forEach(function (n) {
        var dr = n.userData.drift;
        n.position.x += dr.x * dt; n.position.y += dr.y * dt; n.position.z += dr.z * dt;
        if (Math.abs(n.position.x) > 5.2) dr.x *= -1;
        if (Math.abs(n.position.y) > 3.2) dr.y *= -1;
        if (Math.abs(n.position.z) > 3.2) dr.z *= -1;
        n.scale.setScalar(1 + Math.sin(t * 1.3 + n.userData.phase) * 0.3);
      });
      syncLines();

      var orbitR = 8.5, angle = t * 0.08;
      camera.position.x = damp(camera.position.x, Math.sin(angle) * orbitR + mouse.x * 1.1, 2, dt);
      camera.position.z = damp(camera.position.z, Math.cos(angle) * orbitR, 2, dt);
      camera.position.y = damp(camera.position.y, mouse.y * -1.2, 2, dt);
      camera.lookAt(0, 0, 0);

      renderWithBloom(renderer, scene, camera, bloomState);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(size) : null;
    if (ro) ro.observe(el);

    return {
      start: function () { if (raf === null) { lastFrame = null; raf = requestAnimationFrame(frame); } },
      stop: function () { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } },
      dispose: function () {
        this.stop();
        el.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        nodeGeo.dispose();
        nodes.forEach(function (n) { n.material.dispose(); });
        lineGeo.dispose(); lineMat.dispose();
        disposeBloom(bloomState);
        renderer.dispose();
      },
    };
  }

  // ── aurora-mesh: one flowing curtain-style plane, tilted, bloom-forward ──
  function buildAuroraMesh(THREE, canvas, el) {
    var baseHex = themeColor(el, 'scene3dBase', '--rfhq-color-bg', '#16233f');
    var accentHex = themeColor(el, 'scene3dAccent', '--rfhq-color-primary', '#a8672f');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(58, 1, 0.1, 100);
    camera.position.set(0, 0.4, 7);

    var bloomState = null;
    function size() {
      var w = canvas.clientWidth || el.clientWidth || 800;
      var h = canvas.clientHeight || el.clientHeight || 500;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      resizeBloom(bloomState, w, h);
    }
    size();

    var ribbon = makeWaveLayer(THREE, scene, {
      axis: 'v', freq: 0.4, speed: 0.5, amp: 0.9, x: 0, y: -0.5, z: -2, rotX: -0.5,
      w: 24, h: 11, segW: 60, segH: 30, opacity: 0.85, base: baseHex, accent: accentHex, floor: 0.35, gain: 0.4,
    });

    var mouse = { x: 0, y: 0 };
    function onMove(e) {
      var r = el.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) - 0.5;
      mouse.y = ((e.clientY - r.top) / r.height) - 0.5;
    }
    el.addEventListener('pointermove', onMove);

    bloomState = attachBloom(renderer, scene, camera, canvas, { strength: 1.1, radius: 0.6, threshold: 0.55 });

    var raf = null, lastFrame = null;
    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.1) : 0.016;
      lastFrame = now;
      var t = now / 1000;

      ribbon.update(t);
      ribbon.mesh.rotation.z = Math.sin(t * 0.1) * 0.05;

      camera.position.x = damp(camera.position.x, mouse.x * 1.3, 2.5, dt);
      camera.position.y = damp(camera.position.y, 0.4 + mouse.y * 0.5, 2.5, dt);
      camera.lookAt(0, -0.3, -2);

      renderWithBloom(renderer, scene, camera, bloomState);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(size) : null;
    if (ro) ro.observe(el);

    return {
      start: function () { if (raf === null) { lastFrame = null; raf = requestAnimationFrame(frame); } },
      stop: function () { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } },
      dispose: function () {
        this.stop();
        el.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        ribbon.dispose();
        disposeBloom(bloomState);
        renderer.dispose();
      },
    };
  }

  // ── radial-glow: pulsing core + orbiting halo + energy rings ─────────────
  function buildRadialGlow(THREE, canvas, el) {
    var baseHex = themeColor(el, 'scene3dBase', '--rfhq-color-bg', '#2a3f66');
    var accentHex = themeColor(el, 'scene3dAccent', '--rfhq-color-primary', '#a8672f');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(56, 1, 0.1, 100);
    camera.position.z = 6.5;

    var bloomState = null;
    function size() {
      var w = canvas.clientWidth || el.clientWidth || 800;
      var h = canvas.clientHeight || el.clientHeight || 500;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      resizeBloom(bloomState, w, h);
    }
    size();

    var coreGeo = new THREE.IcosahedronGeometry(1.0, 4);
    var coreMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(accentHex) });
    var core = new THREE.Mesh(coreGeo, coreMat);
    scene.add(core);

    var haloGroup = new THREE.Group();
    scene.add(haloGroup);
    var COUNT = 500;
    var pos = new Float32Array(COUNT * 3);
    var col = new Float32Array(COUNT * 3);
    var cAccent = new THREE.Color(accentHex), cBase = new THREE.Color(baseHex).lerp(new THREE.Color(0xffffff), 0.35);
    for (var i = 0; i < COUNT; i++) {
      var r = 1.6 + Math.pow(Math.random(), 1.6) * 3.6;
      var theta = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.cos(theta) * r;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 1.4 * (1 - r / 5.2);
      pos[i * 3 + 2] = Math.sin(theta) * r;
      var c = cAccent.clone().lerp(cBase, Math.min(1, (r - 1.6) / 3.6));
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    }
    var haloGeo = new THREE.BufferGeometry();
    haloGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    haloGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    var haloMat = new THREE.PointsMaterial({
      size: 0.045, vertexColors: true, transparent: true, opacity: 0.8,
      depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    });
    var halo = new THREE.Points(haloGeo, haloMat);
    haloGroup.add(halo);

    var ring1 = new THREE.Mesh(new THREE.TorusGeometry(2.3, 0.006, 8, 128), new THREE.MeshBasicMaterial({ color: new THREE.Color(baseHex).lerp(new THREE.Color(0xffffff), 0.6), transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending }));
    var ring2 = new THREE.Mesh(new THREE.TorusGeometry(3.0, 0.004, 8, 128), new THREE.MeshBasicMaterial({ color: new THREE.Color(accentHex), transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending }));
    ring1.rotation.x = Math.PI / 2.3; ring2.rotation.x = Math.PI / 2.6;
    scene.add(ring1); scene.add(ring2);

    var mouse = { x: 0, y: 0 };
    function onMove(e) {
      var r = el.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) - 0.5;
      mouse.y = ((e.clientY - r.top) / r.height) - 0.5;
    }
    el.addEventListener('pointermove', onMove);

    bloomState = attachBloom(renderer, scene, camera, canvas, { strength: 1.3, radius: 0.7, threshold: 0.5 });

    var raf = null, lastFrame = null;
    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = lastFrame ? Math.min((now - lastFrame) / 1000, 0.1) : 0.016;
      lastFrame = now;
      var t = now / 1000;

      core.scale.setScalar(1 + Math.sin(t * 0.9) * 0.05);
      core.rotation.y += 0.15 * dt;
      haloGroup.rotation.y += 0.05 * dt;
      ring1.rotation.z += 0.08 * dt; ring2.rotation.z -= 0.05 * dt;

      camera.position.x = damp(camera.position.x, mouse.x * 1.6, 2.5, dt);
      camera.position.y = damp(camera.position.y, -mouse.y * 1.1, 2.5, dt);
      camera.lookAt(0, 0, 0);

      renderWithBloom(renderer, scene, camera, bloomState);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(size) : null;
    if (ro) ro.observe(el);

    return {
      start: function () { if (raf === null) { lastFrame = null; raf = requestAnimationFrame(frame); } },
      stop: function () { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } },
      dispose: function () {
        this.stop();
        el.removeEventListener('pointermove', onMove);
        if (ro) ro.disconnect();
        coreGeo.dispose(); coreMat.dispose();
        haloGeo.dispose(); haloMat.dispose();
        ring1.geometry.dispose(); ring1.material.dispose();
        ring2.geometry.dispose(); ring2.material.dispose();
        disposeBloom(bloomState);
        renderer.dispose();
      },
    };
  }

  var SCENES = {
    ambient: buildAmbient,
    starfield: buildStarfield,
    constellation: buildConstellation,
    'aurora-mesh': buildAuroraMesh,
    'radial-glow': buildRadialGlow,
  };

  function mount(el) {
    if (el._scene3d) return;
    var kind = el.dataset.scene3d;
    var build = SCENES[kind];
    if (!build) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'rfhq-scene3d-canvas';
    el.appendChild(canvas);

    loadThree().then(function (THREE) {
      if (!el.isConnected) return; // removed while three.js was loading
      var ctx = build(THREE, canvas, el);
      el._scene3d = ctx;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) ctx.start(); else ctx.stop();
        });
      }, { threshold: 0.01 });
      io.observe(el);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) ctx.stop(); else if (el.isConnected) ctx.start();
      });

      window.rfhqDom.onRemoved(function (removed) {
        if (removed.indexOf(el) !== -1) {
          ctx.dispose();
          io.disconnect();
          delete el._scene3d;
        }
      });
    });
  }

  var NO_SCENE3D_PATHS = [
    '/login', '/register', '/update-password', '/complete-signup',
    '/forgot', '/verify', '/qrcode', '/printview', '/printpreview',
  ];

  function boot() {
    var path = window.location.pathname.replace(/\/$/, '');
    if (NO_SCENE3D_PATHS.indexOf(path) !== -1) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelectorAll('[data-scene3d]').forEach(mount);
    window.rfhqDom.onAdded(function (roots) {
      roots.forEach(function (root) {
        (root.matches && root.matches('[data-scene3d]') ? [root] : [])
          .concat(Array.from(root.querySelectorAll ? root.querySelectorAll('[data-scene3d]') : []))
          .forEach(mount);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
